import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, CheckSquare, FileWarning, AlarmClock, ShieldCheck, Sparkles, Database,
} from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { NextBestActionHero, SignalRow } from '../../components/SignalFeed';
import { SetupChecklist } from '../../components/SetupChecklist';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { useSignals, useSignalsSummary } from '../../hooks/useSignals';
import { useInboxCounts } from '../../hooks/useApInvoices';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useLoadSampleData } from '../../hooks/useSampleData';
import { useToast } from '../../components/ui/Toast';
import { useCan } from '../../hooks/useCan';

export function HomePage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: ws } = useWorkspace(wsId);
  const { data: signals, isLoading } = useSignals(wsId);
  const { data: summary } = useSignalsSummary(wsId);
  const { data: counts } = useInboxCounts(wsId);

  const hero = signals?.[0];
  const rest = signals?.slice(1) ?? [];

  const link = (page: string) => () => navigate(`/app/workspace/${wsId}/${page}`);

  // Pull the per-domain counts straight from the matching signal's params
  // (the backend already computed them) so the KPI tiles agree with the feed.
  const countFor = (kind: string): number => {
    const s = signals?.find((x) => x.kind === kind);
    return s ? Number(s.params.count ?? 0) : 0;
  };

  // A workspace with no signals AND no inbox activity is almost certainly a
  // fresh/empty one — surface the "load sample data" affordance (wired in Wave 3).
  const inboxTotal =
    (counts?.pendingReview ?? 0) + (counts?.dueSoon ?? 0) + (counts?.pastDue ?? 0) + (counts?.disputed ?? 0);
  const looksEmpty = (signals?.length ?? 0) === 0 && inboxTotal === 0;

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="mb-1">
          <h1 className="display text-2xl font-semibold">{t('home.title')}</h1>
        </div>
        <p className="text-sm text-ink-500 mb-6">
          {ws ? t('home.subtitleNamed', { name: ws.name }) : t('home.subtitle')}
        </p>

        <IntroCallout
          storageKey="home"
          title={t('intro.home.title')}
          body={t('intro.home.body')}
        />

        {/* First-run setup checklist — auto-hides when complete/dismissed */}
        <SetupChecklist wsId={wsId} />

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
          <KpiTile
            icon={FileWarning}
            label={t('home.kpi.needsReviewAp')}
            value={countFor('needs_review_ap') || (counts?.pendingReview ?? 0)}
            tone="accent"
            onClick={link('ap-invoices?reviewOnly=1')}
          />
          <KpiTile
            icon={AlarmClock}
            label={t('home.kpi.overdueAr')}
            value={countFor('overdue_ar') || (counts?.pastDue ?? 0)}
            tone="warn"
            onClick={link('collections')}
          />
          <KpiTile
            icon={CheckSquare}
            label={t('home.kpi.pendingApprovals')}
            value={countFor('pending_approval')}
            tone="neutral"
            onClick={link('approvals')}
          />
          <KpiTile
            icon={AlertCircle}
            label={t('home.kpi.openSignals')}
            value={summary?.total ?? 0}
            tone={summary && summary.high > 0 ? 'warn' : 'neutral'}
            onClick={() => hero && navigate(hero.ctaPath)}
            badge={summary && summary.high > 0 ? t('home.kpi.highBadge', { count: summary.high }) : undefined}
          />
        </div>

        {/* Next Best Action feed */}
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-accent-600 dark:text-accent-300" />
          <h2 className="display text-lg font-semibold">{t('home.nextBestAction')}</h2>
        </div>

        {isLoading ? (
          <Card className="p-10 text-center text-ink-500">{t('common.loading')}</Card>
        ) : !signals || signals.length === 0 ? (
          <AllClear looksEmpty={looksEmpty} wsId={wsId} />
        ) : (
          <div className="space-y-4">
            {hero && <NextBestActionHero signal={hero} />}
            {rest.length > 0 && (
              <Card>
                <div className="divide-y divide-ink-200 dark:divide-ink-800">
                  {rest.map((s) => (
                    <SignalRow key={s.id} signal={s} />
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function AllClear({ looksEmpty, wsId }: { looksEmpty: boolean; wsId: string | undefined }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { canMutate } = useCan();
  const load = useLoadSampleData(wsId);

  const onLoad = () => {
    load.mutate(undefined, {
      onSuccess: () => toast.success(t('sampleData.loaded')),
      onError: () => toast.error(t('sampleData.error')),
    });
  };

  return (
    <Card className="p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
        <ShieldCheck size={22} className="text-emerald-600 dark:text-emerald-300" />
      </div>
      <div className="display text-lg font-semibold mb-1">{t('home.allClear')}</div>
      <p className="text-sm text-ink-500 max-w-md mx-auto">{t('home.allClearBody')}</p>
      {looksEmpty && (
        <div className="mt-6">
          <button
            type="button"
            data-testid="home-load-sample-data"
            onClick={onLoad}
            disabled={!canMutate || load.isPending}
            title={canMutate ? undefined : t('common.viewerReadOnly')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Database size={15} />
            {load.isPending ? t('sampleData.loading') : t('home.loadSampleData')}
          </button>
          <p className="text-xs text-ink-400 mt-2">{t('sampleData.description')}</p>
        </div>
      )}
    </Card>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  tone,
  onClick,
  badge,
}: {
  icon: typeof AlertCircle;
  label: string;
  value: number;
  tone: 'accent' | 'neutral' | 'warn' | 'muted';
  onClick: () => void;
  badge?: string;
}) {
  const tones = {
    accent: 'bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200',
    neutral: 'bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200',
    warn: 'bg-amber-50 dark:bg-warn-600/20 text-warn-600',
    muted: 'bg-ink-50 dark:bg-ink-900 text-ink-500',
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left hover:border-accent-300 dark:hover:border-accent-700 transition-colors"
    >
      <Card className="p-4 flex items-center gap-3 h-full">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-ink-500 truncate">{label}</div>
          <div className="num text-xl font-semibold">{value}</div>
          {badge && <div className="text-[10px] text-warn-600 font-medium">{badge}</div>}
        </div>
      </Card>
    </button>
  );
}
